import { Injectable } from '@angular/core';
import { FormField } from '../models/form-field';
import { Util } from '../utils/utils.module';
import { MsgReader } from 'msgreader';
import { Email } from 'typescript-email-parser';

@Injectable()
export class UploadService {
  private contentFileList: any = [];
  private filesToFormMap = null;
  private initialForm = true;
  private securityData = null;
  private requiredFields = [];
  private serverData = null;
  private numberOfFiles: number = null;
  private extRootFolder: any;
  private bExtFolderHasMoreLevels: boolean = false;
  private emptyNameDocumentsCount = 0;
  private isSingleEmptyFile = false;

  constructor() {

  }

  public mapFilesToForm(fileList: any[], desc: any): void {
    const filesToFormMap = {};
    let formName = '';
    let fileName = '';
    for (let i = 0; i < fileList?.length; i++) {
      fileName = fileList[i].name;
      formName = Util.RestAPI.getFormFromFileName(fileName);
      const appID = Util.RestAPI.getAppIDForFile(fileName);
      if (appID === 'DEFAULT') {
        formName = fileName;
      }
      if (!filesToFormMap[formName]) {
        filesToFormMap[formName] = [];
      }
      filesToFormMap[formName].push(fileList[i]);
    }
    this.filesToFormMap = filesToFormMap;
  }

  public extractOnlyNameFromFile(fileName): string {
    const parts: string[] = fileName.split('.');
    parts.pop();
    return parts.length > 0 ? parts.join('.') : fileName;
  }

  public getEmptyNameDocumentsCount(): number {
    if (this.emptyNameDocumentsCount > 0) {
      return this.emptyNameDocumentsCount;
    }
    return 0;
  }

  public setIsSingleEmptyFile(isSingleFileUplaod: boolean): void {
    this.isSingleEmptyFile = isSingleFileUplaod;
  }

  public getIsSingleEmptyFile(): boolean {
    return this.isSingleEmptyFile;
  }

  public setEmptyNameDocumentsCount(count): void {
    this.emptyNameDocumentsCount = count;
  }

  public getNumberOfFiles(): number {
    return this.numberOfFiles;
  }

  public refreshFileToFormMapping(formChangedName: string, prevForm: string, applyAllChecked: boolean): any {
    const filesToFormMap = this.filesToFormMap;
    let formNames;
    for (let i = 0; i < this.contentFileList?.length; i++) {
      formNames = Util.RestAPI.getFormFromFileName(this.contentFileList[i].name, true);
      if (!!(formNames?.find(c => c['%FORM_NAME'] === formChangedName))) {
        if (!filesToFormMap[formChangedName]) {
          filesToFormMap[formChangedName] = [];
        }
        if (filesToFormMap[formChangedName]?.indexOf(this.contentFileList[i]) === -1) {
          filesToFormMap[formChangedName]?.push(this.contentFileList[i]);
        }
        if (filesToFormMap[prevForm]?.indexOf(this.contentFileList[i]) !== -1) {
          filesToFormMap[prevForm]?.splice(filesToFormMap[prevForm].indexOf(this.contentFileList[i]), 1);
        }
      }
    }
    this.filesToFormMap = filesToFormMap;
    return applyAllChecked ? this.filesToFormMap?.[formChangedName] : this.contentFileList;
  }

  public getFilesToFormMap(): any {
    return this.filesToFormMap;
  }

  public setFilesToFormMap(filesToFormMap: any) {
    this.filesToFormMap = filesToFormMap;
  }

  public getFilesToUpload(isGroup: boolean = false): any {
    const files = this.filesToFormMap ? Object.values(this.filesToFormMap)[0] : [];
    return isGroup ? files : this.contentFileList;
  }

  public getContentFileList(): any[] {
    return this.contentFileList;
  }

  public isInitialForm(): boolean {
    return this.initialForm;
  }

  public getSecurityData(): any {
    return this.securityData;
  }

  public setData(contentFileList: any[], desc: any, securityData: any, initialForm: boolean) {
    if (!!contentFileList) {
      this.contentFileList = contentFileList.filter(file => !!this.extractOnlyNameFromFile(file.name));
      this.mapFilesToForm(this.contentFileList, desc);
      this.numberOfFiles = contentFileList?.length;
      this.emptyNameDocumentsCount = this.numberOfFiles - this.contentFileList?.length;
    }
    if (securityData !== undefined) {
      this.securityData = securityData;
    }
    if (initialForm !== undefined) {
      this.initialForm = initialForm;
    }
    if (initialForm) {
      let formName = Object.keys(this.filesToFormMap)[0];
      const validFormName = !!formName ? Util.RestAPI.getFormFromFileName(this.filesToFormMap[formName][0].name) : formName;
      if (formName !== validFormName) {
        formName = validFormName;
      }
      this.getNewRequiredFields(formName, desc).then(fields => {
        this.requiredFields = fields;
      });
    }
  }

  public setServerData(serverData: any) {
    this.serverData = serverData;
  }

  public getServerData(): any {
    return this.serverData;
  }

  public resetData(): void {
    this.contentFileList = [];
    this.filesToFormMap = null;
    this.securityData = null;
    this.initialForm = true;
    this.requiredFields = [];
    this.serverData = null;
  }

  public deleteUploadedFiles(fileNames: any): void {
    const names = Util.deepCopy(fileNames);
    const filesLength = names?.length;
    let form = '';
    if (filesLength > 0 && !!this.filesToFormMap) {
      form = this.getFormNameFromMap(names[0]);
    }
    if (!!this.filesToFormMap && this.contentFileList?.length > 0) {
      for (let i = 0; i < filesLength; i++) {
        if (!form || form === '') {
          form = Util.RestAPI.getFormFromFileName(names[i]);
        }
        if (!!this.filesToFormMap[form]) {
          const index = this.filesToFormMap[form].findIndex(file => file.name === names[i]);
          this.filesToFormMap[form].splice(index, 1);
          if (this.filesToFormMap[form].length === 0) {
            delete this.filesToFormMap[form];
          }
        }
        this.contentFileList.splice(this.contentFileList.findIndex(file => file.name === names[i]), 1);
      }
      const noOfForms = Object.entries(this.filesToFormMap)?.length;
      for (let i = (noOfForms - 1); i >= 0; i--) {
        form = Object.keys(this.filesToFormMap)[i]?.toString();
        if (!!this.filesToFormMap[form] && this.filesToFormMap[form]?.length === 0) {
          delete this.filesToFormMap[form];
        }
      }
    }
  }

  private getFormNameFromMap(fileName: any) {
    for (let i = 0; i < Object.keys(this.filesToFormMap)?.length; i++) {
      const index = this.filesToFormMap[Object.keys(this.filesToFormMap)[i]].findIndex(file => file.name === fileName);
      if (index > -1) {
        return Object.keys(this.filesToFormMap)[i];
      }
    }
  }

  public getRequiredFields(): any[] {
    return this.requiredFields;
  }

  public setRequiredFields(fields: any[]) {
    this.requiredFields = fields;
  }

  public getNewRequiredFields(formName: any, desc: any): Promise<any> {
    const requiredFields = [];
    let fields = [];
    let newField = null;
    let newSubField = null;
    let subFields = [];
    const lib = !!desc && !!desc.lib ? desc.lib : Util.RestAPI.getPrimaryLibrary();
    return new Promise(resolve => {
      Util.RestAPI.getFormTemplate(formName, lib).toPromise().then(template => {
        if (!!template) {
          fields = template.defs;
          for (const field of fields) {
            newField = new FormField(field, 'profile');
            if (newField.isRequired) {
              requiredFields.push(newField.name);
            }
            if (!!field.fields) {
              subFields = field.fields;
              for (const subField of subFields) {
                newSubField = new FormField(subField, 'profile');
                if (newSubField.isRequired) {
                  requiredFields.push(newSubField.name);
                }
              }
            }
          }
          resolve(requiredFields);
        }
      });
    });
  }

  public fileNameForUpload(applyAllChecked: boolean, kind: string, fileList: File[], filePaths: any[], dataFiles: any[], copyFileName: string, decodeFileName: boolean = true): string {
    let names: string = null;
    let nFiles: number = fileList && fileList.length > 0 ? fileList.length : filePaths && filePaths.length > 0 ? filePaths.length : dataFiles ? dataFiles.length : 0;
    if (nFiles) {
      nFiles = applyAllChecked ? nFiles : 1;
      for (let i = 0; i < nFiles; i++) {
        let name: string;
        if (fileList && fileList.length > 0) {
          const file: File = fileList[i];
          name = file.name;
        } else if (filePaths && filePaths.length > 0) {
          name = Util.RestAPI.getFileNameFromPath(filePaths[i]);
          if (decodeFileName && kind === 'profile_checkin') {
            const docObj = Util.RestAPI.decodeFileName(name);
            if (!!docObj) {
              name = docObj.name + (!!docObj.ext ? '.' + docObj.ext : '');
            }
          }
        } else if (dataFiles && dataFiles.length > 0) {
          name = dataFiles[i].name;
        }
        if (name) {
          const parts: string[] = name.split('.');
          if (parts.length > 1) {
            parts.splice(parts.length - 1, 1);
            name = parts.join('.');
          }
          name = name.replace(/,; /g, '_');//replace(', ', '__');
          if (names) {
            names += Util.RestAPI.kMultiFileSeparator + name;
          } else {
            names = name;
          }
        }
      }
    } else if (copyFileName) {
      names = copyFileName;
    }
    return names;
  }

  public updateAttachNum(serverData: any, dataFiles: any[], emailHasAttachments: boolean): any {
    const nDataFiles = dataFiles.length;
    serverData['MSG_ITEM'] = '1';
    const firstAttachNum: number = dataFiles[0].attachNum;
    if (dataFiles.findIndex(item => item['url'] === 'promise_msg') !== -1 && nDataFiles > 1) {
      serverData['ATTACH_NUM'] = '';
      for (let i = 0; i < nDataFiles; i++) {
        if (dataFiles[i]['url'] === 'promise_msg') {
          serverData['ATTACH_NUM'] += !emailHasAttachments ? '0' : '-1';
        } else {
          serverData['ATTACH_NUM'] += (dataFiles[i].attachNum + 1).toString();
        }
        serverData['MAIL_ID'] = !!serverData['MAIL_ID'] ? serverData['MAIL_ID'] : dataFiles[i].mailID;
        if (i < nDataFiles - 1) {
          serverData['ATTACH_NUM'] += Util.RestAPI.kMultiFileSeparator;
        }
      }
    } else if (firstAttachNum !== undefined && firstAttachNum !== null) {
      if (firstAttachNum === -1) {
        serverData['ATTACH_NUM'] = !emailHasAttachments ? '0' : '-1';
        serverData['MAIL_ID'] = !!serverData['MAIL_ID'] ? serverData['MAIL_ID'] : dataFiles[0].mailID;
      } else {
        serverData['ATTACH_NUM'] = '';
        for (let i = 0; i < nDataFiles; i++) {
          const dataFile = dataFiles[i];
          const curAttachNum: number = dataFile.attachNum + 1;
          serverData['ATTACH_NUM'] += curAttachNum.toString();
          serverData['MAIL_ID'] = dataFile.mailID;
          if (i < nDataFiles - 1) {
            serverData['ATTACH_NUM'] += Util.RestAPI.kMultiFileSeparator;
          }
        }
      }
    } else {
      serverData['ATTACH_NUM'] = !emailHasAttachments ? '0' : '-1';
    }
    return serverData;
  }


  public requiredFieldsSatisfied(fields: any[]): boolean {
    if (fields.every(field => this.requiredFields.includes(field))) {
      return true;
    } else {
      const serverData = this.getServerData();
      const extraFields = fields.filter(field => !this.requiredFields.includes(field));
      this.requiredFields = this.requiredFields.concat(extraFields);
      for (const field of extraFields) {
        if (!serverData || !serverData[field]) {
          return false;
        }
      }
      return true;
    }
  }

  public isApplyToAllChecked(): boolean {
    return this.numberOfFiles > 1 ? true : false;
  }

  public updateDocumentsFormInfo(formName: string, appID: string, serverData: any, library: string) {
    const formIdByFormName: string = Util.RestAPI.getProfileFormID(formName, library);
    const defaultProfileForm: string = Util.RestAPI.getDefaultProfileForm(library);
    if (!appID || formName !== defaultProfileForm?.['%FORM_NAME']) {
      this.setDocumentsFormInfo(serverData, formName, formIdByFormName);
      return;
    }

    const defaultFormId: string = '0';
    const appIds: string[] = appID.split(Util.RestAPI.kMultiFileSeparator);

    const appAndFormMapping: { [id: string]: string; } = {};
    const forms: string[] = [];
    let isDefaultForm: boolean = true;

    for (let i = 0; i < appIds.length; i++) {
      let formIdByApp: string;
      if (appAndFormMapping[appIds[i]]) {
        formIdByApp = appAndFormMapping[appIds[i]];
      } else {
        formIdByApp = Util.RestAPI.getProfileFormsForApp(appIds[i], library).filter(form => form['%FORM_APPLICATION'] === '%PRIMARY_FORM')[0] ? defaultFormId : formIdByFormName;
        appAndFormMapping[appIds[i]] = formIdByApp;
      }
      forms[i] = formIdByApp === defaultFormId ? '%PRIMARY_FORM' : formName;
      isDefaultForm = isDefaultForm && formIdByApp === defaultFormId;
    }
    this.setDocumentsFormInfo(serverData, forms.join(Util.RestAPI.kMultiFileSeparator), isDefaultForm ? defaultFormId : formIdByFormName);
  }

  private setDocumentsFormInfo(serverData: any, formName: string, formId: string) {
    if (!serverData) {
      return;
    }
    if ('_restapi' in serverData) {
      serverData['_restapi']['form_name'] = formName;
    }
    if (formId) {
      serverData['FORM'] = formId;
    }
  }

  public setExtRootFolder(rootFolder) {
    this.extRootFolder = rootFolder;
  }

  public updateExtFolderHasMoreLevels(hasMoreFolders) {
    this.bExtFolderHasMoreLevels = hasMoreFolders;
  }

  public extFolderHasMoreLevels(): boolean {
    return this.bExtFolderHasMoreLevels;
  }

  public deleteExtRootFolderAfterImport() {
    const extAppInfo: any = Util.RestAPI.findExternalApp(Util.RestAPI.getAppDesc()?.lib);
    if (!!extAppInfo && this.getExtAppDeletePreference(extAppInfo['apptype']) && !this.extFolderHasMoreLevels()) {
      Util.RestAPI.delete(this.extRootFolder).subscribe((response) => {
        this.setExtRootFolder(null);
        Util.RestAPI.refresh();
      });
    }
  }

  public getExtAppDeletePreference(appType): boolean {
    return appType === 'onedrive' ? Util.RestAPI.getPreference('delete_original') === '1' : Util.RestAPI.getPreference('delete_original_teams') === '1';
  }

  public getFileExtension(fileName: string): string {
    const parts = fileName ? fileName.split('.') : [];
    const fileExt = parts.length > 1 ? parts.pop()?.toUpperCase() : null;
    return fileExt;
  }


  private getRecipients(recipients: any, type: string): string {
    return recipients?.filter(r => r.recipType === type)?.map(a => a['smtpAddress']).join(',') || '';
  }

  private getEmlRecipients(recipients: any): string {
    const recList = recipients && recipients.length > 0 ? recipients.map(a => a['email']) : [];
    return recList.length > 0 ? recList.join(',') : '';
  }

  public parseEmailFile(file: File) {
    let mailInfo = null;
    const ext = this.getFileExtension(file.name);
    if (!file || !ext) {
      return mailInfo;
    }
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = (e) => {
      const arrayBuffer = e.target!.result as ArrayBuffer;
      const decodedInfo = new TextDecoder('utf-8').decode(arrayBuffer);

      if (ext === 'MSG' || ext === 'OFT') {
        const msg = new MsgReader(arrayBuffer);
        const msgInfo = msg.getFileData();
        mailInfo = {
          'from': msgInfo.creatorSMTPAddress ?? '',
          'to': this.getRecipients(msgInfo.recipients, 'to'),
          'cc': this.getRecipients(msgInfo.recipients, 'cc'),
          'bcc': this.getRecipients(msgInfo.recipients, 'bcc'),
          'sent': Util.Transforms.formatDate(msgInfo.clientSubmitTime),
          'received': Util.Transforms.formatDate(msgInfo.messageDeliveryTime || msgInfo.clientSubmitTime)
        };
      } else if (ext === 'EML') {
        const msgInfo = Email.parse(decodedInfo);
        const date = Util.Transforms.formatDate(`${msgInfo.orig_date?.year}-${msgInfo.orig_date?.month}-${msgInfo.orig_date?.day} ${msgInfo.orig_date.hour}:${msgInfo.orig_date.minute}:${msgInfo.orig_date.second} EST${msgInfo.orig_date.zone}`);
        mailInfo = {
          'from': msgInfo.from && msgInfo.from.length > 0 ? msgInfo.from[0].email ?? '' : '',
          'to': this.getEmlRecipients(msgInfo.to),
          'cc': this.getEmlRecipients(msgInfo.cc),
          'bcc': this.getEmlRecipients(msgInfo.bcc),
          'sent': date,
          'received': date
        };
      }
      file['mailInfo'] = mailInfo;
    };
  }
}
